import {
  Autocomplete,
  CircularProgress,
  FormControl,
  FormGroup,
  FormHelperText,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { AxiosError } from "axios";
import { FieldMetaProps } from "formik";
import { FocusEventHandler, useContext, useEffect, useState } from "react";
import { EmployeeStatus, User } from "../../../app/backendTypes";
import { HttpClientContext, X_REQUEST_ID } from "../../../app/client";

function MultiUserPicker(props: {
  initialValue?: number | undefined;
  meta?: FieldMetaProps<string>;
  onChange: (value: User | null) => void;
  onLoad?: (value: User | null) => void;
  onBlur?: FocusEventHandler;
  disabled?: boolean;
  small?: boolean;
  vc_only?: boolean;
  disableCreate?: boolean;
  disableClearable?: boolean;
  placeholder?: string;
}) {
  const {
    initialValue,
    meta,
    onChange,
    onLoad,
    onBlur,
    disabled,
    small = false,
    vc_only = false,
    disableClearable = false,
    placeholder,
  } = props;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [users, setUsers] = useState<User[]>([]);

  const client = useContext(HttpClientContext);
  useEffect(() => {
    client
      ?.get<User[]>("/api/users")
      .then((res) =>
        setUsers(
          vc_only ? res.data.filter((u) => u.vc_id !== undefined) : res.data
        )
      )
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client, vc_only]);

  const value = users.find((user) => user.employee.id === initialValue);
  useEffect(() => {
    if (value) onLoad && onLoad(value);
  }, [onLoad, value]);

  const theme = useTheme();

  return (
    <>
      <FormControl fullWidth sx={{ maxWidth: "sm" }}>
        <FormGroup>
          <Autocomplete
            disableClearable={disableClearable}
            size={small ? "small" : "medium"}
            disabled={disabled || loading || Boolean(error)}
            options={users}
            value={value || null}
            getOptionLabel={(user) =>
              `${user.employee.username} (${user.employee.id})`
            }
            renderOption={(props, user) => (
              <Typography {...props}>
                <Typography
                  component="span"
                  sx={{
                    textDecorationLine: user.employee.status===EmployeeStatus.PERMANENT
                      ? "none"
                      : "line-through",
                  }}
                >
                  {user.employee.username}
                </Typography>
                &nbsp;
                <Typography component="span" color="text.secondary">
                  ({user.employee.id})
                </Typography>
              </Typography>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                placeholder={
                  loading
                    ? "Loading..."
                    : error
                    ? "Error"
                    : placeholder || "Pick a user"
                }
                error={
                  (meta?.touched && Boolean(meta?.error)) || Boolean(error)
                }
                sx={{
                  input: {
                    "&::placeholder": {
                      color:
                        (meta?.touched && Boolean(meta?.error)) ||
                        Boolean(error)
                          ? theme.palette.error.main
                          : "text.secondary",
                      opacity: 1,
                    },
                  },
                }}
              />
            )}
            onChange={(_, value) => onChange(value)}
            onBlur={onBlur}
          />

          {meta?.touched && Boolean(meta?.error) && (
            <FormHelperText error>{meta.error}</FormHelperText>
          )}
        </FormGroup>
      </FormControl>
    </>
  );
}

export default MultiUserPicker;
